import { Children, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'

import colors from 'components/utils/colors'
import { StyledTableRow } from './TableRow'
import { uniqueId } from 'utils/strings'

export const StyledTableBody = styled.tbody`
  text-align: left;
  text-transform: none;
  border-bottom: 1px solid ${colors.secondary95};

  & > ${StyledTableRow} {
    &:nth-child(odd) {
      background-color: ${props => props.darkBg ? colors.secondary90 : colors.secondary95};
    }

    &:nth-child(even) {
      background-color: ${props => props.darkBg ? colors.secondary80 : 'white'};
    }

    ${props => props.hoverable && css`
      &:not(table > tbody > tr *):hover {
        background-color: ${colors.primary90};
      }
    `}

    ${props => props.clickable && css`
      &:hover {
        cursor: pointer;
      }
    `}
  }
`

const TableBodyDnd = ({ children, hoverable = true, clickable, onDragEnd }) => {
  const id = useMemo(() => uniqueId(), [])
  const droppableId = 'droppableTable_' + id

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={droppableId}>
        {(droppableProvided, droppableSnapshot) => {
          return (
            <StyledTableBody
              ref={droppableProvided.innerRef}
              hoverable={hoverable}
              clickable={clickable}
            >
              {Children.map(children, (child, index) => {
                if (!child) {
                  return null
                } else if (!child.props.id) {
                  console.error('Please specify id for all table rows in draggable TableBody')
                  return null
                }

                return (
                  <Draggable
                    key={child.props.id}
                    draggableId={child.props.id}
                    index={index}
                  >
                    {(provided, snapshot) => {
                      return (
                        <child.type
                          innerRef={provided.innerRef}
                          {...child.props}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          {child.props.children}
                        </child.type>
                      )
                    }}
                  </Draggable>
                )
              })}
              {droppableProvided.placeholder}
            </StyledTableBody>
          )
        }}
      </Droppable>
    </DragDropContext>
  )
}

TableBodyDnd.propTypes = {
  children: PropTypes.node,
  hoverable: PropTypes.bool,
  clickable: PropTypes.bool,
  onDragEnd: PropTypes.func,
}

const TableBody = ({ children, hoverable = true, clickable, draggable, onDragEnd, darkBg, className }) => {
  if (draggable) {
    return (
      <TableBodyDnd hoverable={hoverable} clickable={clickable} onDragEnd={onDragEnd} className={className}>
        {children}
      </TableBodyDnd>
    )
  }

  return (
    <StyledTableBody hoverable={hoverable} clickable={clickable} darkBg={darkBg} className={className}>
      {children}
    </StyledTableBody>
  )
}

TableBody.propTypes = {
  children: PropTypes.node,
  hoverable: PropTypes.bool,
  clickable: PropTypes.bool,
  draggable: PropTypes.bool,
  onDragEnd: PropTypes.func,
  darkBg: PropTypes.bool,
}

export default TableBody
